import React, { useState, useEffect, useMemo } from "react";
import {
    FormControl, TextField, Typography, Select, MenuItem
} from "@mui/material";
import {
    exprToStringWithCron,
    stringToExprWithCron,
} from "services/CVT.js";

function ptypeToRegex(ptype) {
    switch(ptype) {
    case 'item': return /^[~]*[-a-zA-Z0-9]*$/;
    case 'tag': return /^[a-zA-Z][0-9a-zA-Z_]*$/;
    case 'number': return /^[0-9]*$/;
    case 'float': return /^[0-9.]*$/;
    default: return /^.*$/
    }
}

function ConstrainedTextField({value, ptype, placeholder, readOnly, onChange}) {
    const handleChange = (value) => {
        //empty always allowed
        console.log("REG",ptype,  ptypeToRegex(ptype));
        if (value === "" || ptypeToRegex(ptype).test(value)) {
            onChange(value);            
        }
    };
    return (
        <TextField
            variant="standard"
            type="string"
            value={ value }
            disabled={readOnly}
            placeholder={ placeholder }
            InputLabelProps={{ sx: { fontSize: 10 } }}
            InputProps={{ sx: { fontSize: 10 } }}
            onChange={(event) => handleChange(event.target.value)}
        />
    );
}

function TagChooser({
    index,
    value,
    param, // [default, type]
    readOnly,
    handleParamChange
}) {
    const und = value?.indexOf("_");
    const und1 = param[0]?.indexOf("_");    
    const stack = value?.slice(0,und) || "";
    const defStack = param[0]?.slice(0,und1);    
    const tag = value?.slice(und+1) || "";
    const defTag = param[0]?.slice(und1+1);        
    const setTag = (v) => {
        if (/^[a-zA-Z0-9_]*$/.test(v)) {
            const n = (stack||defStack)+"_"+(v||defTag);
            console.log("SET TAG", v, n);
            handleParamChange(index, (n !== "_" && n !== param[0]) ? n : "");
        }  
    };
    const setStack = (v) => {
        const n = (v||defStack)+"_"+(tag||defTag);
        console.log("SET stack", v, n);        
        handleParamChange(index, (n !== "_" && n !== param[0]) ? n : "");        
    };
    return (<tr>
                <td> <Typography variant="subtitle2" style={{ fontSize: 10 }}> {index} </Typography></td>
                <td>
                    <ConstrainedTextField
                        ptype="item"
                        value={ stack }
                        readOnly={readOnly}
                        placeholder={ defStack || "<same stack>" }
                        onChange={setStack}
                    />
                </td>
                <td>
                    <ConstrainedTextField
                        ptype="tag"                        
                        value={ tag }
                        readOnly={readOnly}                       
                        placeholder={ "default tag id: " + (defTag || "<no default>") }
                        onChange={setTag}
                    />
                </td>
            </tr>);
}

function ParamChooser(props) {
    const { readOnly, value, index, param, // [value, default, type]
            handleParamChange } = props;
    return ((param?.[1] === 'tag') ?
            <TagChooser {...props} /> : 
            <tr>
                <td> <Typography variant="subtitle2" style={{ fontSize: 10 }}> {index} </Typography></td>
                <td>
                    <ConstrainedTextField
                        ptype={param[1] || ""}
                        value={ "" + (value || "") }
                        placeholder={ "" + (param[0] || "") }
                        readOnly={ readOnly }                    
                        onChange={(v) => handleParamChange(index, v)}
                    />
                </td>
            </tr>); 
}
            
//Notes on how this works:
//  This component takes the JSON expr form ('derived') which is a parse tree in json from of expression.
//  That is not very user friendly, so there is a bunch of code here that parses that into a string form
//    that is like an excel formula.  That string form ('parsed') is what the user can edit directly - and
//    then it is unparsed to the JSON form and setDerived is called.
//  Now - there are some predefined settings: ('predefined') that are parameterized.  The params use the "P"
//    function that can be defined as "params" in a expression node (usually at the top)
//      P( name, default_value, optional_type )

const TagValueCalculation = ({
  derived,
  setDerived,
  readOnly,
  predefined,
  advanced = false
}) => {
    const setDerivedIfChanged = useMemo(() => {
        return (v) => {
            const oldV = derived || "";
            console.log("OLD:", oldV, "NEW:", v);
            if(v !== oldV) setDerived(v);
        }
    }, [derived, setDerived]);
    //todo also match the choice if there params and tag params are different values
    const [parseError, setParseError] = useState(false);
    const [parsed, setParsed] = useState();
    const [choice, setChoice] = useState('unknown'); //whichChoice(derived));
    const [depParams, setDepParams] = useState({}); // name -> [ default, type, ... ]
    const [editDerived, setEditDerived] = useState(derived);
    const [custom, setCustom] = useState(false);
    const derivedParams = useMemo(() => { //todo what do we do if we can't parse
        try {
            return JSON.parse(derived)?.params || {};
        } catch {
            return {}
        }
    }, [derived]);
    const handleParamChange = (k, v) => {
        function processVal(v, args) {
            if(args?.[1] === "int") return parseInt(v);
            else if(args?.[1] === "float") return parseFloat(v);
            else if(args?.[1] === "number") return Number(v);            
            return v;
        }
        const newParams = {...derivedParams}
        if(v == null) {
            delete newParams[k];
        } else {
            newParams[k] = processVal(v, depParams[k]);
        }
        setDerivedIfChanged(JSON.stringify({...JSON.parse(derived), params: newParams}));
    };
    useEffect(() => {
        console.log("UE DERIVED", derived, choice);
        if(derived) {
            try {
                const expr = JSON.parse(derived);
                const availableParams = {}
                const p = exprToStringWithCron(expr, availableParams);
                const ch = ((d) => {
                    for(let i=0; i<predefined.length; i++) {
                        if(predefined[i].expr === d) return i;
                    }
                    return "custom";
                }) (p);
                setChoice(ch);
                console.log("setting  parsed...", derived, JSON.stringify(p), ch);
                setParsed(p);
                setParseError(false);
                setDepParams(availableParams);
            } catch (err) {
                console.log("DER ERR", err);
                setDepParams({});
            } 
        } else {
            setChoice("From Device");
            setDepParams({});                
        }
        setEditDerived(derived);
    }, [derived]); //Todo: figure out if "choice" needs to be in dependencies
    
    function changeParsed(newParsed) {
        try {
            const newExprNoParams = (newParsed == null || newParsed === "") ? "" : stringToExprWithCron(newParsed);
            console.log("RESULT COMPILE", newExprNoParams);
            const newExpr = (derivedParams && Object.keys(derivedParams).length > 0) ? {...newExprNoParams, params: derivedParams } : newExprNoParams;
            const newDerived = newExpr === "" ? "" : JSON.stringify(newExpr);
            //double encode since JSON dump options might have been different
            const oldDerived = derived ? JSON.stringify(JSON.parse(derived)) : "";
            if(oldDerived !== newDerived) {
                console.log("set derived again")
                console.log(JSON.stringify(oldDerived));
                console.log(JSON.stringify(newDerived));
                setDerivedIfChanged(newDerived);
            } else {
                setParsed(newParsed);
            }
            if(parseError) {
                setParseError(false);
            }
        } catch(err) {
            console.log(err);
            setParseError(true);
            setParsed(newParsed);
        }
    }
    function chooseChoice(ch) {
        console.log("CHOOSING", ch);
        switch(ch) {
        case 'From Device':
            setCustom(false);            
            setDerivedIfChanged("");
            break;
        case 'custom':
            setCustom(true);
            break;
        default:
            const expr = predefined[ch]?.expr;
            console.log("EXPR CH", expr);
            if(expr) {
                setCustom(false);
                setDerivedIfChanged(JSON.stringify(stringToExprWithCron(expr)));
            }
        }
    };
    function changeEditDerived(newEditDerived) {
        if(newEditDerived === "") {
            setDerivedIfChanged("")
        } else {
            try {
                setDerivedIfChanged(JSON.stringify(JSON.parse(editDerived)));
            } catch {
                setEditDerived(newEditDerived);
                //todo: should be an error sign here
            }
        }
    };
    
    return (
        advanced ?
        <TextField
                variant="standard"
                label="JSON Formatted Raw Tag Value Formula (blank or empty for device sensor)"
                multiline
                fullWidth
                error={ derived !== editDerived }            
                type="string"
                value={ editDerived }
                disabled={ readOnly }
                InputLabelProps={{ sx: { fontSize: 12 } }}
                InputProps={{ sx: { fontSize: 12 } }}
                onChange={(event) => changeEditDerived(event.target.value)}
            />
        : (<>
            <FormControl fullWidth>
                <Select
                    labelId="choices-label"
                    size="small"
                    value={custom ? "custom" : choice}
                    onChange={(e) => chooseChoice(e.target.value)}
                    disabled={ readOnly }                    
                >
                    <MenuItem value="From Device">From Device (no calculation)</MenuItem>
                    { predefined && predefined.map((v,idx) => { return (<MenuItem value={idx}>{v.name}</MenuItem>) }) }
                    <MenuItem value="custom">Custom...</MenuItem>
                    <MenuItem value="unknown" style={{ display: "none" }}>Loading...</MenuItem>                    
                </Select>
            </FormControl>
            { custom && <TextField
                variant="standard"
                label="Tag Value Formula (blank for device sensor)"
                fullWidth
                multiline
                type="string"
                error={parseError}
                disabled={ readOnly }                                                             
                value={ parsed }
                InputLabelProps={{ sx: { fontSize: 12 } }}
                InputProps={{ sx: { fontSize: 12 } }}
                onChange={(event) => changeParsed(event.target.value)} />
            }
            <div>
                { !custom && Number.isInteger(choice) && <Typography variant="subtitle2" style={{ fontSize: 10 }}>
                                                  {predefined[choice]?.description || ""}
                                              </Typography> }
                { !!Object.keys(depParams).length &&
                  <table>
                      <tbody>
                          { Object.entries(depParams).map((kv) =>
                              <ParamChooser readOnly={readOnly} index={kv[0]}
                                            value={derivedParams[kv[0]] || null}
                                            param={kv[1] || null}
                                            handleParamChange={handleParamChange}/>)
                          }
                      </tbody>
                  </table>
                }
            </div>
           </>)
    )
}

export default TagValueCalculation;

          
